import React, { useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCopy,
  faTimes,
  faLink,
} from "@fortawesome/pro-light-svg-icons";

import Button from "ui/Button";
import { useUnmounted } from "utils/hooks";

const copyToClipboard = (text: string): Promise<void> => {
  if (navigator.clipboard) {
    return navigator.clipboard.writeText(text);
  }
  // navigator.clipboard is only available in secure contexts
  // use the legacy document.execCommand("copy") as a fallback
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  try {
    document.execCommand("copy");
    return Promise.resolve();
  } catch (err) {
    return Promise.reject(err);
  } finally {
    document.body.removeChild(textarea);
  }
};

export type Props = {
  className?: string;
  content: string | (() => string | Promise<string>);
  label?: string;
  title?: string;
  variant?: "text" | "link";
};

const CopyToClipboard: React.FunctionComponent<Props> = ({
  className,
  content,
  label,
  title,
  variant,
}) => {
  const unmounted = useUnmounted();
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(null);

  const handleCopy = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const actualContent = await (typeof content === "function"
      ? content()
      : content);
    copyToClipboard(actualContent)
      .then(() => {
        if (unmounted.current) {
          return;
        }

        setCopied(true);
      })
      .catch((err: Error) => {
        setCopyError(err.message);
      })
      .finally(() => {
        if (unmounted.current) {
          return;
        }

        setTimeout(() => {
          if (unmounted.current) {
            return;
          }

          setCopied(null);
        }, 2000);
      });
  };

  let copyIcon;
  let copyTitle;
  if (copyError) {
    copyIcon = <FontAwesomeIcon icon={faTimes} />;
    copyTitle = copyError;
  } else if (copied) {
    copyIcon = <FontAwesomeIcon icon={faCheck} />;
    copyTitle = "Copied!";
  } else {
    copyIcon = <FontAwesomeIcon icon={variant === "link" ? faLink : faCopy} />;
    copyTitle = title;
  }

  return (
    <Button
      onClick={(e) => void handleCopy(e)}
      className={className}
      variant="link"
      bare
      title={copyTitle}
    >
      {copyIcon}
      {label && <> {label}</>}
    </Button>
  );
};

export default CopyToClipboard;
